import type { DescriptionLang, PoStatus } from "@hisense/shared";
import type { PoLineDraft } from "./PoLinesEditor";
import { PO_DEFAULT_PAYMENT_TERMS, PO_DEFAULT_SHIPPING_METHOD, vnDate } from "@hisense/shared";

/*
 * Bố cục trang Sửa đơn mua (19/09/2026, cùng chuẩn PI, báo giá và đơn hàng mẫu).
 *
 * Production 19/09/2026 mới có MỘT đơn mua (bản nháp từ hệ cũ), nên không có số liệu dùng để chọn cột như ở PI hay báo giá. Bố
 * cục theo nghiệp vụ: mã, mô tả, màu, cỡ, MOQ lấy từ danh mục (chỉ đọc); người mua nhập LOT, số lượng, đơn vị, giá. Ghi chú dòng
 * là cột phụ — ẩn khi không dòng nào dùng, tự hiện và không ẩn được khi có dòng dùng.
 *
 * Bảng này là nguồn của bố cục; test bắt buộc mọi trường phần đầu và mọi trường của dòng có đúng một chỗ.
 */

/** Phần đầu đơn mua lúc tạo mới — mặc định như hệ cũ (PurchaseOrder.js initialFormData). */
export const emptyPoHeader = () => ({
  status: "Draft" as PoStatus,
  currency: "USD",
  supplierId: "",
  supplierContact: "",
  orderDate: vnDate(),
  expectedDeliveryDate: "",
  actualDeliveryDate: "",
  priority: "Medium",
  paymentTerms: PO_DEFAULT_PAYMENT_TERMS,
  shippingMethod: PO_DEFAULT_SHIPPING_METHOD,
  shipping: "",
  taxPercentage: "",
  remarks: "",
  printLanguage: "en" as DescriptionLang,
});
export type PoHeaderDraft = ReturnType<typeof emptyPoHeader>;

export type PoGroupId = "options" | "supplier" | "order" | "lines" | "notes";

/**
 * Trường phần đầu theo nhóm. Phí vận chuyển và thuế nằm ở khối tổng tiền dưới bảng hàng (như phụ thu / chiết khấu của PI). PI liên
 * kết là trạng thái riêng của trang (không nằm trong phần đầu), ở mục gập của nhóm Đơn hàng.
 */
export const PO_HEADER_GROUPS: readonly { id: PoGroupId; fields: readonly (keyof PoHeaderDraft)[] }[] = [
  { id: "options", fields: ["printLanguage"] },
  { id: "supplier", fields: ["supplierId", "supplierContact"] },
  { id: "order", fields: ["orderDate", "expectedDeliveryDate", "actualDeliveryDate", "status", "currency", "paymentTerms", "shippingMethod", "priority"] },
  { id: "lines", fields: ["shipping", "taxPercentage"] },
  { id: "notes", fields: ["remarks"] },
];

/** Cột luôn hiện, theo thứ tự bản in. Thành tiền là cột tính, không mang trường nào. */
export const PO_MAIN_COLUMNS = [
  "productCode", "subRef", "description", "color", "size", "moq", "lotNumber", "quantity", "unit", "unitPrice", "amount",
] as const;
export const PO_SIDE_COLUMNS = ["remarks"] as const;
export type PoColumn = (typeof PO_MAIN_COLUMNS)[number] | (typeof PO_SIDE_COLUMNS)[number];

/** Trường của dòng mà mỗi cột mang. Cột mô tả hiện bản của ngôn ngữ in và tên nhà cung cấp gắn trên dòng. */
export const PO_COLUMN_FIELDS: Record<PoColumn, readonly (keyof PoLineDraft)[]> = {
  productCode: ["productCode"], subRef: ["subRef"], description: ["description", "descriptionZh", "supplierName"], color: ["color"],
  size: ["size"], moq: ["moq"], lotNumber: ["lotNumber"], quantity: ["quantity"], unit: ["unit"], unitPrice: ["unitPrice"], amount: [],
  remarks: ["remarks"],
};
/** Trường của dòng KHÔNG có ô: máy giữ và gửi ngược nguyên vẹn khi lưu — id dòng, lựa chọn đơn vị & giá, sản phẩm, dòng PI nguồn. */
export const PO_LINE_AUTOMATIC = ["id", "unitSelection", "productId", "piItemId"] as const;

/** Độ rộng cố định từng cột (rem); `null` là cột mô tả, lấy phần còn lại (tối thiểu 14rem). Cột chính cộng 62,5rem. */
export const PO_COLUMN_WIDTH: Record<PoColumn, number | null> = {
  productCode: 7, subRef: 5.5, description: null, color: 6.5, size: 5, moq: 4.5, lotNumber: 7, quantity: 5.5, unit: 5, unitPrice: 5.5,
  amount: 7, remarks: 12,
};
export const PO_DESCRIPTION_MIN_REM = 14;
/** Cột số thứ tự và cột nút xoá dòng. */
export const PO_EDGE_COLUMNS_REM = 2 + 2;

/** Có dòng nào dùng cột phụ không — có thì cột phụ hiện và không ẩn được. */
export const poSideColumnsUsed = (lines: readonly PoLineDraft[]) =>
  lines.some((l) => PO_SIDE_COLUMNS.some((k) => String(l[k] ?? "").trim() !== ""));
